<template>
  <div class="space-y-6">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-2">Default locale</label>
        <Select
          v-model="form.defaultLocale"
          :options="localeOptions"
          optionLabel="label"
          optionValue="value"
          class="w-full"
          :disabled="disabled"
        />
      </div>

      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-2">Default prompt mode</label>
        <Select
          v-model="form.promptModeDefault"
          :options="promptModeOptions"
          optionLabel="label"
          optionValue="value"
          class="w-full"
          :disabled="disabled"
        />
      </div>

      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-2">Default profiling mode</label>
        <Select
          v-model="form.profilingModeDefault"
          :options="profilingModeOptions"
          optionLabel="label"
          optionValue="value"
          class="w-full"
          :disabled="disabled"
        />
      </div>

      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-2">Review policy</label>
        <Select
          v-model="form.reviewPolicy"
          :options="reviewPolicyOptions"
          optionLabel="label"
          optionValue="value"
          class="w-full"
          :disabled="disabled"
        />
      </div>

      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-2">Bootstrap assessment schema</label>
        <Select
          v-model="form.bootstrapSchemaId"
          :options="schemaOptions"
          optionLabel="label"
          optionValue="value"
          showClear
          placeholder="None (optional)"
          class="w-full"
          :disabled="disabled"
        />
        <p class="text-xs text-gray-500 mt-1">
          Optional filter for the admin baseline bootstrap. Leave empty to use all submitted dashboard assessments.
        </p>
      </div>

      <div class="field flex items-center gap-3 pt-6">
        <ToggleSwitch v-model="form.persistDimensionReports" :disabled="disabled" />
        <label class="text-sm font-medium text-gray-700">Persist dimension reports</label>
      </div>
    </div>

    <div class="field">
      <label class="block text-sm font-medium text-gray-700 mb-2">Coachable dimensions</label>
      <MultiSelect
        v-model="form.dimensionValues"
        :options="dimensionOptions"
        optionLabel="label"
        optionValue="value"
        display="chip"
        filter
        placeholder="All dimensions (leave empty)"
        class="w-full"
        :disabled="disabled"
      />
      <p class="text-xs text-gray-500 mt-1">Empty selection = all tenant dimensions are coachable.</p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-2">Min. engagement characters</label>
        <InputNumber v-model="form.minEngagementChars" :min="10" :max="500" class="w-full" :disabled="disabled" />
      </div>
      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-2">Min. profiling answers before complete</label>
        <InputNumber
          v-model="form.minProfilingAnswersBeforeComplete"
          :min="1"
          :max="10"
          class="w-full"
          :disabled="disabled"
        />
      </div>
    </div>

    <div class="field">
      <label class="block text-sm font-medium text-gray-700 mb-2">Dimension complete marker</label>
      <InputText v-model="form.dimensionCompleteMarker" class="w-full" :disabled="disabled" />
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-2">Coaching ready message (DE)</label>
        <Textarea v-model="form.coachingReadyMessage.de" rows="3" class="w-full" autoResize :disabled="disabled" />
      </div>
      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-2">Coaching ready message (EN)</label>
        <Textarea v-model="form.coachingReadyMessage.en" rows="3" class="w-full" autoResize :disabled="disabled" />
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-2">System prompt prefix (DE)</label>
        <Textarea v-model="form.systemPromptPrefix.de" rows="3" class="w-full" autoResize :disabled="disabled" />
        <p class="text-xs text-gray-500 mt-1">Optional tone/branding prepended to every coaching prompt.</p>
      </div>
      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-2">System prompt prefix (EN)</label>
        <Textarea v-model="form.systemPromptPrefix.en" rows="3" class="w-full" autoResize :disabled="disabled" />
      </div>
    </div>

    <div class="border-t pt-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-1">Prompt templates</h3>
      <p class="text-sm text-gray-600 mb-4">
        Customize LLM prompt bodies per template. Placeholders use the
        <code class="text-xs bg-gray-100 px-1 rounded">&#123;&#123;name&#125;&#125;</code>
        syntax and are listed for each template below.
      </p>

      <Accordion :multiple="true" class="prompt-templates-accordion">
        <AccordionPanel
          v-for="template in templateOptions"
          :key="template.key"
          :value="template.key"
        >
          <AccordionHeader>
            <span class="font-medium">{{ template.label }}</span>
            <Tag
              v-if="isTemplateCustomized(template.key)"
              value="Customized"
              severity="info"
              class="ml-2"
            />
          </AccordionHeader>
          <AccordionContent>
            <p class="text-xs text-gray-500 mb-3">
              Placeholders:
              <code
                v-for="placeholder in template.placeholders"
                :key="placeholder"
                class="mr-2 text-xs bg-gray-100 px-1 rounded"
              >{{ formatPlaceholder(placeholder) }}</code>
            </p>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div class="field">
                <label class="block text-sm font-medium text-gray-700 mb-2">German (DE)</label>
                <Textarea
                  v-model="form.promptTemplates[template.key].de"
                  rows="8"
                  class="w-full font-mono text-sm"
                  autoResize
                  :disabled="disabled"
                />
              </div>
              <div class="field">
                <label class="block text-sm font-medium text-gray-700 mb-2">English (EN)</label>
                <Textarea
                  v-model="form.promptTemplates[template.key].en"
                  rows="8"
                  class="w-full font-mono text-sm"
                  autoResize
                  :disabled="disabled"
                />
              </div>
            </div>

            <Button
              label="Reset to default"
              icon="pi pi-refresh"
              severity="secondary"
              size="small"
              :disabled="disabled || !isTemplateCustomized(template.key)"
              @click="resetTemplate(template.key)"
            />
          </AccordionContent>
        </AccordionPanel>
      </Accordion>
    </div>
  </div>
</template>

<script>
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import ToggleSwitch from 'primevue/toggleswitch';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import {
  COACHING_CONFIG_LOCALE_OPTIONS,
  COACHING_CONFIG_PROFILING_MODE_OPTIONS,
  COACHING_CONFIG_PROMPT_MODE_OPTIONS,
  COACHING_CONFIG_REVIEW_POLICY_OPTIONS,
  COACHING_PROMPT_TEMPLATE_OPTIONS,
} from '@/utils/coachingConfigOptions';

export default {
  name: 'CoachingConfigFields',
  components: {
    Select,
    MultiSelect,
    ToggleSwitch,
    InputNumber,
    InputText,
    Textarea,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    Button,
    Tag,
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
    promptTemplateDefaults: {
      type: Object,
      default: () => ({}),
    },
    dimensionOptions: {
      type: Array,
      default: () => [],
    },
    schemaOptions: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localeOptions: COACHING_CONFIG_LOCALE_OPTIONS,
      promptModeOptions: COACHING_CONFIG_PROMPT_MODE_OPTIONS,
      profilingModeOptions: COACHING_CONFIG_PROFILING_MODE_OPTIONS,
      reviewPolicyOptions: COACHING_CONFIG_REVIEW_POLICY_OPTIONS,
      templateOptions: COACHING_PROMPT_TEMPLATE_OPTIONS,
    };
  },
  methods: {
    formatPlaceholder(name) {
      return `{{${name}}}`;
    },
    isTemplateCustomized(key) {
      const current = this.form.promptTemplates?.[key];
      const defaults = this.promptTemplateDefaults?.[key];
      if (!current || !defaults) {
        return false;
      }
      const deChanged = (current.de ?? '').trim() !== (defaults.de ?? '').trim();
      const enChanged = (current.en ?? '').trim() !== (defaults.en ?? '').trim();
      return deChanged || enChanged;
    },
    resetTemplate(key) {
      const defaults = this.promptTemplateDefaults?.[key];
      if (!defaults || !this.form.promptTemplates?.[key]) {
        return;
      }
      this.form.promptTemplates[key].de = defaults.de ?? '';
      this.form.promptTemplates[key].en = defaults.en ?? '';
    },
  },
};
</script>
